import { useState, type ReactNode } from "react";
import { Loader2, Search } from "lucide-react";

import { BetaFeatureOnly } from "@/components/beta-feature";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { ScrollbarArea } from "@/toggle-ui-kit/scrollbar";
import { searchTextIncludes } from "@/utils/search-text";

interface ExistingReportPickerProps<TReport> {
  title: string;
  description: string;
  reports: TReport[];
  selectedReportId: string | null;
  onSelect: (reportId: string | null, report: TReport | null) => void;
  getReportId: (report: TReport) => string;
  /** 지정하면 목록 위에 검색 입력이 생기고, 반환 문자열 기준으로 필터링한다. 검색은 베타 기능이다 */
  getSearchText?: (report: TReport) => string;
  searchPlaceholder?: string;
  showSelectionIndicator?: boolean;
  renderReportTitle: (report: TReport) => ReactNode;
  renderReportMeta?: (report: TReport) => ReactNode;
  renderSelectedReport?: (report: TReport) => ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage: string;
  errorMessage?: string;
  onRetry?: () => void;
  className?: string;
  /**
   * 목록 높이. `"cap"`(기본) 은 256px 에서 끊고, `"fill"` 은 부모의 남은 높이를 채운다
   * (부모가 높이를 정해 줘야 한다 — 카드 높이가 고정된 화면용).
   *
   * 클래스가 아니라 두 값 중 하나로 받는 이유: Radix ScrollArea 는 Root 와 Viewport
   * 두 상자에 서로 다른 제약을 걸어야 스크롤이 생긴다. Root 에 `max-h` 만 주면
   * Viewport 의 `h-full` 이 `auto` 로 풀려 넘치지 않고, Root 의 `overflow-hidden` 이
   * 잘라 버린다(스크롤바도 안 나온다). 호출부가 틀릴 수 없도록 조합을 여기서 정한다.
   */
  listHeight?: "cap" | "fill";
  /**
   * 선택 상태 색. `"mono"`(기본) 는 primary-base, `"blue"` 는 테두리 Primary/Color +
   * 내부 blue-50 — 텍스트가 많은 화면에서 "지금 고른 항목"을 눈에 띄게 하려고 쓴다.
   *
   * 클래스가 아니라 두 값 중 하나로 받는 이유: 행·인디케이터 테두리·내부 점 세 군데가
   * 같은 톤으로 움직여야 하는데, 호출부가 클래스로 덮으면 마크업이 바뀔 때 조용히
   * 한쪽만 색이 어긋난다(`listHeight` 와 같은 이유).
   */
  selectedTone?: "mono" | "blue";
}

const SELECTED_TONE_CLASS = {
  mono: {
    row: "border-primary-base bg-primary-base/8",
    ring: "border-primary-base",
    dot: "bg-primary-base",
  },
  blue: {
    row: "border-primary-color bg-blue-50",
    ring: "border-primary-color",
    dot: "bg-primary-color",
  },
} as const;

export const ExistingReportPicker = <TReport,>({
  title,
  description,
  reports,
  selectedReportId,
  onSelect,
  getReportId,
  getSearchText,
  searchPlaceholder = "이름으로 검색",
  showSelectionIndicator = false,
  renderReportTitle,
  renderReportMeta,
  renderSelectedReport,
  isLoading = false,
  isError = false,
  emptyMessage,
  errorMessage = "목록을 불러오는 중 오류가 발생했습니다.",
  onRetry,
  className,
  listHeight = "cap",
  selectedTone = "mono",
}: ExistingReportPickerProps<TReport>) => {
  const toneClass = SELECTED_TONE_CLASS[selectedTone];
  // 목록이 높이를 채우면 로딩·에러·빈 상태도 같이 채워야 카드 안이 비어 보이지 않는다
  const stateFillClass =
    listHeight === "fill" &&
    "min-h-0 flex-1 flex items-center justify-center text-center";
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  const filteredReports =
    getSearchText && trimmedQuery
      ? reports.filter((report) =>
          searchTextIncludes(getSearchText(report), trimmedQuery),
        )
      : reports;
  const selectedReport = reports.find(
    (report) => getReportId(report) === selectedReportId,
  );
  const searchInput = getSearchText ? (
    <BetaFeatureOnly>
      <div className="relative mb-2 w-full shrink-0">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-label-alternative"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 w-full rounded-[8px] border border-line-normal py-1.5 pl-9 pr-3 body-2-normal-regular text-label-normal placeholder:text-label-alternative focus:border-primary-base focus:outline-none"
        />
      </div>
    </BetaFeatureOnly>
  ) : null;

  return (
    <div
      className={cn(
        "flex flex-1 min-w-0 flex-col rounded-[10px] border-dashed border-2 border-neutral-200 bg-white p-4 box-content",
        // min-h-0 없이는 flex item 의 min-height 가 auto(=콘텐츠 높이)라 부모를 넘친다
        listHeight === "fill" && "min-h-0",
        className,
      )}
    >
      {(title || description) && (
        <div className="mb-2 flex items-center justify-between">
          <span className="label-1-normal-medium text-label-normal">
            {title}
          </span>
          <span className="caption-2-regular text-label-alternative">
            {description}
          </span>
        </div>
      )}

      {isLoading ? (
        <div
          data-report-picker-state
          className={cn(
            "flex items-center justify-center py-10 text-label-alternative",
            stateFillClass,
          )}
        >
          <Loader2 className="mr-2 size-4 animate-spin" />
          <span className="body-2-normal-regular">목록을 불러오는 중...</span>
        </div>
      ) : isError ? (
        <div
          data-report-picker-state
          className={cn(
            "flex flex-col items-center justify-center gap-4 py-10 text-center",
            stateFillClass,
            // 세로로 쌓인 내용이라 fill 이 걸어 주는 flex-row 를 되돌린다
            listHeight === "fill" && "flex-col",
          )}
        >
          <p className="body-2-normal-medium text-status-negative">
            {errorMessage}
          </p>
          {onRetry && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={onRetry}
            >
              다시 시도
            </Button>
          )}
        </div>
      ) : reports.length === 0 ? (
        <div
          data-report-picker-state
          className={cn(
            "py-10 text-center body-2-normal-regular text-label-alternative",
            stateFillClass,
          )}
        >
          {emptyMessage}
        </div>
      ) : (
        <>
          {searchInput}
          {filteredReports.length === 0 ? (
            <div
              data-report-picker-state
              className={cn(
                "py-10 text-center body-2-normal-regular text-label-alternative",
                stateFillClass,
              )}
            >
              검색 결과가 없습니다.
            </div>
          ) : (
            // 스크롤바 노출은 Radix 기본값(hover) — 저장소의 다른 ScrollbarArea 와 같다.
            <ScrollbarArea
              data-report-picker-list
              // 높이 상한은 Viewport 가, 부모 채우기는 Root 가 맡는다 (listHeight 주석 참고)
              className={cn(
                "min-w-0",
                listHeight === "fill" && "min-h-0 flex-1",
              )}
              // [&>div]: Radix 가 끼우는 display:table 래퍼를 block 으로 — 안 그러면 행의 truncate 가 죽는다
              viewportClassName={cn(
                "[&>div]:!block",
                listHeight === "cap" && "max-h-64",
              )}
            >
              <ul className="flex min-w-0 flex-col gap-1">
                {filteredReports.map((report) => {
                  const reportId = getReportId(report);
                  const isSelected = reportId === selectedReportId;

                  return (
                    <li key={reportId} className="min-w-0 shrink-0">
                      <button
                        type="button"
                        data-report-option
                        aria-pressed={isSelected}
                        onClick={() =>
                          onSelect(
                            isSelected ? null : reportId,
                            isSelected ? null : report,
                          )
                        }
                        className={cn(
                          "flex min-h-12 w-full min-w-0 items-center gap-3 overflow-hidden rounded-[8px] border px-3 py-2 text-left transition-colors",
                          isSelected
                            ? toneClass.row
                            : "border-line-normal bg-background-base hover:bg-background-neutral-neutral",
                        )}
                      >
                        {showSelectionIndicator && (
                          <span
                            aria-hidden
                            className={cn(
                              "flex size-4 shrink-0 items-center justify-center rounded-full border",
                              isSelected
                                ? toneClass.ring
                                : "border-line-normal",
                            )}
                          >
                            {isSelected && (
                              <span
                                className={cn(
                                  "size-2 rounded-full",
                                  toneClass.dot,
                                )}
                              />
                            )}
                          </span>
                        )}
                        {/* w-0: 긴 제목의 min-content 폭 대신 버튼의 남은 폭만 사용한다. */}
                        <div className="w-0 flex-1">
                          <div className="max-w-full truncate body-2-normal-medium text-label-normal">
                            {renderReportTitle(report)}
                          </div>
                          {renderReportMeta && (
                            <div className="caption-2-regular text-label-alternative">
                              {renderReportMeta(report)}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </ScrollbarArea>
          )}
          {renderSelectedReport && selectedReport && (
            <div
              data-report-picker-selected
              className="mt-2 shrink-0 rounded-[8px] bg-background-neutral-alternative p-3"
            >
              {renderSelectedReport(selectedReport)}
            </div>
          )}
        </>
      )}
    </div>
  );
};
